import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { 
  Bell, 
  Users, 
  Calendar, 
  CheckCircle, 
  TrendingUp,
  RefreshCw,
  LogOut,
  Phone,
  Mail
} from "lucide-react";
import { agentAdvancer33 } from './images';
import ImpersonationBanner from "./components/ImpersonationBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardCliente({ agenziaId: propAgenziaId }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [agenziaId] = useState(propAgenziaId || searchParams.get('agenzia_id') || searchParams.get('token'));
  const [impersonationSession, setImpersonationSession] = useState(null);

  useEffect(() => {
    if (agenziaId) {
      loadDashboardData();
      
      // Check for impersonation session
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session');
      if (sessionId) {
        loadImpersonationSession(sessionId);
      }
    } else {
      toast.error('ID Agenzia non valido');
      navigate('/');
    }
  }, [agenziaId, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API}/dashboard-cliente/${agenziaId}`);
      if (!response.ok) {
        throw new Error('Errore nel caricamento dashboard');
      }
      
      const data = await response.json();
      setDashboardData(data);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Errore nel caricamento dei dati dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRinnovaServizio = () => {
    toast.success('Richiesta di rinnovo servizio inviata! Ti contatteremo presto.');
  };

  const loadImpersonationSession = async (sessionId) => {
    try {
      const response = await fetch(`${API}/admin/impersonation-data/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setImpersonationSession(data.session_info);
      }
    } catch (error) {
      console.error("Error loading impersonation session:", error);
    }
  };

  const handleReturnToAdmin = () => {
    if (impersonationSession) {
      // Termina la sessione di impersonation
      fetch(`${API}/admin/impersonation/${impersonationSession.session_id}`, {
        method: 'DELETE'
      }).catch(error => console.error("Error ending impersonation:", error));
    }
    // Reindirizza al pannello admin
    window.location.href = '/admin';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center">
        <Card className="w-full max-w-md shadow-xl rounded-2xl">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-600">Accesso negato</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Token di accesso non valido o agenzia non trovata.</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Torna alla home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-blue-50 to-emerald-50 min-h-screen">
      
      {/* Banner Impersonation */}
      {impersonationSession && (
        <div className="mb-6">
          <ImpersonationBanner 
            userName={impersonationSession.impersonated_user_name}
            userType="agenzia"
            onReturnToAdmin={handleReturnToAdmin}
          />
        </div>
      )}
      
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.location.reload()}
            className="hover:scale-105 transition-transform cursor-pointer"
            title="Torna alla Dashboard"
          >
            <img 
              src={agentAdvancer33} 
              alt="InLeads Logo" 
              className="w-14 h-14 object-contain"
            />
          </button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              👋 Benvenuto {dashboardData.agenzia_nome}
            </h1>
            <p className="text-blue-700 mt-2 font-medium">Dashboard Cliente</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 shadow-lg"
        >
          <LogOut className="w-4 h-4" />
          Esci
        </Button>
      </div>

      {/* Statistiche principali - Cards con numeri grandi */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <Card className="shadow-lg bg-white border-0">
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{dashboardData.statistiche.totale_candidati}</div>
            <div className="text-sm text-gray-600">Tot. Candidati</div>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg bg-white border-0">
          <CardContent className="p-6 text-center">
            <Calendar className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{dashboardData.statistiche.colloqui_fissati}</div>
            <div className="text-sm text-gray-600">Colloqui</div>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg bg-white border-0">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{dashboardData.statistiche.inserimenti}</div>
            <div className="text-sm text-gray-600">Inserimenti</div>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg bg-white border-0">
          <CardContent className="p-6 text-center">
            <TrendingUp className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{dashboardData.statistiche.colloqui_acquistati}</div>
            <div className="text-sm text-gray-600">Colloqui Acquistati</div>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg bg-white border-0">
          <CardContent className="p-6 text-center">
            <RefreshCw className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-800">{dashboardData.statistiche.colloqui_utilizzati}</div>
            <div className="text-sm text-gray-600">Colloqui Utilizzati</div>
          </CardContent>
        </Card>
      </div>

      {/* Andamento Mensile - Grafico */}
      <Card className="shadow-lg bg-white border-0 mb-8">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">📈 Andamento Mensile</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboardData.andamento_mensile}>
              <XAxis dataKey="mese" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="colloqui" fill="#3b82f6" name="Colloqui" radius={[4, 4, 0, 0]} />
              <Bar dataKey="inserimenti" fill="#10b981" name="Inserimenti" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Candidati Recenti - Tabella */}
        <Card className="lg:col-span-3 shadow-lg bg-white border-0">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-800">👥 Candidati Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.candidati.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessun candidato assegnato ancora</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-semibold text-gray-700">Nome</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700">Contatto</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700">Stato</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.candidati.map((candidato, idx) => (
                      <tr key={idx} className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors`}>
                        <td className="py-4 px-2 font-medium">{candidato.nome} {candidato.cognome}</td>
                        <td className="py-4 px-2">
                          <div className="flex flex-col text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {candidato.telefono}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {candidato.email}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-2">
                          <span className="text-sm">{candidato.stato}</span>
                        </td>
                        <td className="py-4 px-2 text-sm text-gray-600">{candidato.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifiche - Box laterale */}
        <Card className="shadow-lg bg-white border-0">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-600" /> Notifiche
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboardData.notifiche.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nessuna notifica</p>
            ) : (
              dashboardData.notifiche.map((notifica, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg text-sm ${
                    notifica.tipo === 'success' ? 'bg-emerald-50 border-l-4 border-emerald-400' :
                    notifica.tipo === 'warning' ? 'bg-yellow-50 border-l-4 border-yellow-400' :
                    notifica.tipo === 'critical' ? 'bg-red-50 border-l-4 border-red-400' :
                    'bg-blue-50 border-l-4 border-blue-400'
                  }`}
                >
                  <span className="font-medium">{notifica.colore}</span> {notifica.messaggio}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Azione Cliente - Bottone Rinnovo */}
      <Card className="shadow-lg bg-white border-0 mt-8">
        <CardContent className="p-6 text-center">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-800 mb-2">📌 Azione Cliente</h3>
            <p className="text-gray-600">Servizio in scadenza o necessario rinnovo pacchetto colloqui?</p>
          </div>
          <Button 
            onClick={handleRinnovaServizio}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all"
          >
            🔄 Richiedi Rinnovo Servizio
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}